/**
 * 🛥️ YACHT IMAGE MANAGEMENT DIALOG
 * Full-featured dialog for managing yacht images
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import YachtImageUploader from './YachtImageUploader';
import { adminYachtService } from '@/services/adminYachtService';

interface YachtImageManagementDialogProps {
  open: boolean;
  onClose: () => void;
  yachtId: string;
  yachtName: string;
}

export const YachtImageManagementDialog: React.FC<YachtImageManagementDialogProps> = ({
  open,
  onClose,
  yachtId,
  yachtName,
}) => {
  const [loading, setLoading] = useState(false);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [pendingImages, setPendingImages] = useState<any[]>([]);

  // Load existing images when dialog opens
  useEffect(() => {
    if (open && yachtId) {
      loadExistingImages();
    }
  }, [open, yachtId]);

  const loadExistingImages = async () => {
    setLoading(true);
    try {
      const response = await adminYachtService.getYachtImages(yachtId);
      if (response.success && response.data) {
        setExistingImages(response.data.map(img => ({
          id: img.id,
          url: img.url,
          category: img.category || img.type || 'exterior',
          isPrimary: img.isPrimary,
          isNew: false,
        })));
      }
    } catch (error) {
      console.error('Failed to load images:', error);
      // Don't show error toast - might be endpoint not implemented yet
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (images: any[]) => {
    const newImages = images.filter(img => img.isNew);

    if (newImages.length === 0) {
      toast.error('No new images to upload');
      return;
    }

    const imageData = newImages.map(img => ({
      url: img.url,
      category: img.category,
      isPrimary: img.isPrimary,
    }));

    const response = await adminYachtService.uploadYachtImages(yachtId, imageData);


    if (response.success) {
      toast.success(`Successfully uploaded ${newImages.length} image(s)!`);
      await loadExistingImages(); // Reload to get server URLs
      setPendingImages([]);
    } else {
      console.error('❌ Upload failed:', response.error);
      toast.error(response.error || 'Upload failed');
      // Don't throw - just show the error to user
    }
  };

  const handleClose = () => {
    // Only warn about NEW images that haven't been uploaded
    const newImages = pendingImages.filter(img => img.isNew);
    if (newImages.length > 0) {
      if (!window.confirm(`You have ${newImages.length} unsaved image(s). Close without uploading?`)) {
        return;
      }
    }
    setPendingImages([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Manage Yacht Images</Typography>
            <Typography variant="body2" color="text.secondary">
              {yachtName}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} edge="end">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>📸 Yacht Photo Management:</strong> Upload, view, and manage yacht images. Images are automatically compressed to &lt;800KB. You can change the cover image at any time by clicking the star icon!
              </Typography>
            </Alert>

            <YachtImageUploader
              yachtId={yachtId}
              existingImages={existingImages}
              onImagesChange={(images) => {
                setPendingImages(images);
              }}
              onUpload={handleUpload}
              maxImages={15}
              maxSizeKB={800}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {pendingImages.filter(img => img.isNew).length > 0 ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default YachtImageManagementDialog;
